import _ from 'lodash';
import { PipeDefinitionUtility } from 'v2/common/quantConfig/index';
import { memo, useCallback, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import { Controller } from 'react-hook-form';
import ErrorMesssage from 'v2/common/components/ErrorMessage/index';
import { useSelector } from 'react-redux';
import { getSelectorBasedStoreValue } from 'v2/modules/withRunForm/redux/selector';
import { onChangeGlobalSegment } from 'v2/modules/withRunForm/events/globalSegment';
import { referenceTracker } from 'v3/quantConfig/reference/referenceTracker';
import { REFERENCE_LIST } from 'v2/common/constants';
import CustomTypeAhead from 'v3/common/components/CustomTypeAhead';

const propTypes = {
  type: PropTypes.string.isRequired,
  onSetNewPipe: PropTypes.func.isRequired,
  validator: PropTypes.shape({}),
  control: PropTypes.shape({}).isRequired,
  errors: PropTypes.shape({}).isRequired,
  pipeIndex: PropTypes.number,
};

const defaultProps = { validator: {}, pipeIndex: undefined };

const PipeSelector = ({ type, onSetNewPipe, validator, control, errors, pipeIndex }) => {
  const typeaheadRef = useRef(null);

  const uiConfigs = useSelector((state) => getSelectorBasedStoreValue(state, 'quantConfig.uiConfigs'));
  const uniqueId = useMemo(() => _.uniqueId('pipe_type_'), []);
  const errorMessage = _.get(errors, `${uniqueId}.message`, '');

  const checkIsPipeRefered = useMemo(() => {
    const pipeEntity = {
      type: REFERENCE_LIST.pipe,
      index: pipeIndex,
    };

    return referenceTracker.checkReferencePresent(pipeEntity);
  }, [pipeIndex]);

  const onChangePipe = useCallback(
    (result, onChange) => {
      if (_.isEmpty(result)) return null;

      const newIndicatorType = _.get(result, [0, 'value']);

      const { pipeConfigs: newPipeConfigs } = onChangeGlobalSegment({
        pipeConfigs: [PipeDefinitionUtility.getPipeConfigDefaults(newIndicatorType)],
        uiConfigs,
      });

      onSetNewPipe(newPipeConfigs[0]);
      return onChange(result);
    },
    [onSetNewPipe, uiConfigs]
  );

  const validatorConfig = useMemo(
    () => ({
      ...validator,
      validate: (value) => {
        if (_.isEmpty(value)) return 'Please add indicator type';

        return null;
      },
    }),
    [validator]
  );

  const selectedOptions = useMemo(() => (type ? [type] : []), [type]);

  return (
    <div className="indicator-type-input">
      <label className="col-form-label col-form-label-sm ">Indicator Type</label>

      <div className="inside-icon">
        <div className="search-icon">
          <i className="fa-light fa-magnifying-glass fa-search-icon secondary "></i>
        </div>

        <ErrorMesssage message={errorMessage}>
          <Controller
            render={({ onChange, name }) => (
              <>
                <CustomTypeAhead
                  disabled={checkIsPipeRefered}
                  ref={typeaheadRef}
                  size="sm"
                  className="w-100"
                  clearButton={!_.isEmpty(selectedOptions)}
                  name={name}
                  customKey={name}
                  defaultSelected={selectedOptions}
                  options={PipeDefinitionUtility.getPipeConfigSearchTerms()}
                  onChange={(result) => {
                    onChangePipe(result, onChange);
                    if (result?.length === 0) {
                      typeaheadRef.current?.focus();
                      typeaheadRef.current?.toggleMenu();
                    }
                  }}
                  filterBy={(option, { text }) => {
                    const normalize = (str) => str.replace(/\s+/g, '').toLowerCase();
                    return normalize(option.label).includes(normalize(text));
                  }}
                />
              </>
            )}
            control={control}
            defaultValue={selectedOptions}
            name={uniqueId}
            rules={validatorConfig}
          />
        </ErrorMesssage>
      </div>
    </div>
  );
};

PipeSelector.propTypes = propTypes;
PipeSelector.defaultProps = defaultProps;

export default memo(PipeSelector);
